import { getEnvironment } from '../../helpers';
import { useUserStore } from '../../stores';
import { getLookerHostUrl } from '../lookerConfig';
import { getRuntimeAppConfig } from '../runtimeConfig';
import type { WorkspaceEnvironmentKeys } from './workspaces';

export const AI_API_BASE_URL_V5 = `/v5/ai`;
export const AI_API_BASE_URL_V7 = `/v7/ai`;
export const STUDIO_API_BASE_URL_V5 = `/v5/studio`;
export const WORKSPACE_API_BASE_URL_V7 = `/v7/workspaces`;
export const REPORTING_API_BASE_URL_V7 = `/v7/reporting`;
export const TASKS_API_BASE_URL_V7 = `/v7/tasks`;
export const LIFE_API_BASE_URL_V5 = `/v5/life`;
export const MICROSERVICES_API_BASE_URL = `/v7/bi-microservices`;
export const HIERARCHIES_API_BASE_URL_V7 = `/v7/taxonomies`;
export const MEDSCAPE_API_BASE_URL_V5 = `/v5/medscape`;
export const MEDSCAPE_API_BASE_URL_V7 = `/v7/medscape`;
export const MEDIA_EFFECTIVENESS_API_BASE_URL_V5 = `/v5/hcp-dtc`;
export const CLINICAL_INSIGHTS_API_BASE_URL_V5 = `/v5/clinical_insights`;
export const LIST_MATCHING_API_BASE_URL_V5 = `/v5/list-matching`;
export const LIST_MATCHING_API_BASE_URL_V7 = `/v7/list-matching`;
export const BI_METADATA_API_BASE_URL_V7 = `/v7/bi_metadata`;
export const MEDIA_EFFECTIVENESS_API_BASE_URL_V7 = `${BI_METADATA_API_BASE_URL_V7}/hcp_dtc_dictionaries`;
export const DTC_API_BASE_URL = `/v7/dtc`;
export const FILTER_API_BASE_URL = `/v7/filters`;

export type ServiceName = 'studioApi' | 'identityBroker' | 'ws';
type ApiConfig = Record<ServiceName, string>;

export const IdentityBrokerClientId: Record<WorkspaceEnvironmentKeys | 'local', string> = {
  local: 'studio-dev',
  dev: 'studio-dev',
  stg: 'studio-stg',
  prod: 'studio-prod',
};

export const ApiConfigs: Record<WorkspaceEnvironmentKeys | 'local', ApiConfig> = {
  local: {
    studioApi: 'http://localhost:8080',
    ws: 'http://localhost:3000',
    identityBroker: 'https://p2-auth-dev.pulsepoint.com',
  },
  dev: {
    studioApi: 'https://bi-api-dev.pulsepoint.com',
    ws: 'https://bi-api.pulsepoint.com',
    identityBroker: 'https://p2-auth-dev.pulsepoint.com',
  },
  stg: {
    studioApi: 'https://bi-api-stg.pulsepoint.com',
    ws: 'wss://bi-api.pulsepoint.com',
    identityBroker: 'https://p2-auth-stg.pulsepoint.com',
  },
  prod: {
    studioApi: 'https://bi-api.pulsepoint.com',
    ws: 'wss://bi-api.pulsepoint.com',
    identityBroker: 'https://p2-auth.pulsepoint.com',
  },
};

export const getApiConfig = (): ApiConfig => {
  // Cloud Run runtime injection: `/config.js` populates `window.APP_CONFIG`
  // before the bundle boots. Present in prod, absent in local dev (falls
  // through to Looker-URL detection).
  const runtime = getRuntimeAppConfig();
  if (runtime?.studioApi) {
    return {
      studioApi: runtime.studioApi,
      identityBroker: runtime.identityBroker ?? ApiConfigs.prod.identityBroker,
      ws: runtime.wsMultichannelUrl ?? ApiConfigs.prod.ws,
    };
  }

  const userStore = useUserStore.getState();

  // Priority: user store (populated after workspace bootstrap) →
  // `window.LOOKER_CONFIG.hostUrl` supplied by the Angular host / dev
  // harness → current page URL as a last resort.
  const instanceURL = userStore.lookerURL || getLookerHostUrl() || window.location.href || '';
  const env = getEnvironment(instanceURL) || 'prod';

  return ApiConfigs[env] || ApiConfigs.dev;
};
